import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { IMAGES } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { breadcrumbSchema } from "@/lib/schema";
import { storyIntro, storyTimeline, storySections, storyMission } from "@/data/story";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OrderButton } from "@/components/TrackedLinks";

const Timeline = ({ lang }) => (
  <ol className="grid md:grid-cols-3 gap-5" data-testid="story-timeline">
    {storyTimeline[lang].map((step, i) => (
      <li key={step.title} className="relative bg-white rounded-2xl p-6 border border-hacienda-border-light" data-testid={`story-timeline-${i}`}>
        <span className="text-hacienda-terracotta text-xs uppercase tracking-[0.2em] font-semibold">{step.label}</span>
        <h3 className="font-serif text-2xl text-hacienda-charcoal font-semibold mt-2 mb-2">{step.title}</h3>
        <p className="text-hacienda-gray text-sm leading-relaxed">{step.text}</p>
      </li>
    ))}
  </ol>
);

const StorySection = ({ section, index, image }) => (
  <article className={`grid lg:grid-cols-5 gap-8 lg:gap-14 items-start ${index % 2 ? "lg:[&>figure]:order-first" : ""}`} data-testid={`story-section-${index}`}>
    <div className="lg:col-span-3">
      <h2 className="font-serif text-3xl md:text-4xl text-hacienda-charcoal font-semibold mb-5">{section.title}</h2>
      <div className="space-y-4 text-hacienda-gray leading-relaxed text-[17px]">
        {section.paragraphs.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
      </div>
    </div>
    <figure className="lg:col-span-2 lg:sticky lg:top-28">
      <img src={image.src} alt={image.alt} loading="lazy" decoding="async" width="600" height="800" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3] lg:aspect-[3/4]" />
    </figure>
  </article>
);

const sectionImages = [
  { src: IMAGES.quesabirria, alt: "Quesabirria tacos — the dish that started at a family stand" },
  { src: IMAGES.softTacos, alt: "Slow-cooked birria de res with corn tortillas and consomé" },
  { src: IMAGES.tacosDorados, alt: "Crispy tacos dorados served Tijuana-style with consomé" },
  { src: IMAGES.birriaPizza, alt: "Birria pizza made to share with family" },
];

export const StoryPage = () => {
  const { t, lang } = useLang();
  return (
    <div data-testid="story-page">
      <Seo
        title="Our Story | Tijuas Tacos Birrieria Albuquerque"
        description="From a pandemic food stand outside our mobile home to a food truck at Unser & Central to our Osuna Road restaurant in 2024—the family story behind Tijuas Tacos Birrieria and our Tijuana-style birria de res."
        path="/our-story"
        jsonLd={breadcrumbSchema([{ label: t("nav.story"), to: "/our-story" }])}
      />
      <Breadcrumbs items={[{ label: t("nav.story"), to: "/our-story" }]} />

      <section className="py-16 px-4" data-testid="story-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("story.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-6 leading-[1.05]">{t("story.title")}</h1>
            <p className="text-hacienda-gray text-lg leading-relaxed" data-testid="story-intro">{storyIntro[lang]}</p>
          </div>
          <img src={IMAGES.quesabirriaPlate} alt="Quesabirria tacos with consomé at Tijuas Tacos Birrieria" width="800" height="1066" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3] lg:aspect-[4/5]" />
        </div>
      </section>

      <section className="py-14 px-4 bg-hacienda-cream" data-testid="story-journey">
        <div className="max-w-6xl mx-auto"><Timeline lang={lang} /></div>
      </section>

      <section className="py-20 px-4" data-testid="story-sections">
        <div className="max-w-6xl mx-auto space-y-24">
          {storySections[lang].map((section, i) => <StorySection key={section.title} section={section} index={i} image={sectionImages[i]} />)}
        </div>
      </section>

      <section className="py-20 px-4 bg-hacienda-charcoal text-hacienda-cream" data-testid="story-mission">
        <div className="max-w-4xl mx-auto text-center">
          <Heart size={32} className="text-hacienda-amber mx-auto mb-5" aria-hidden="true" />
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-6">{t("story.mission")}</h2>
          <div className="space-y-4 text-hacienda-cream/85 text-lg leading-relaxed">
            {storyMission[lang].map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 text-center" data-testid="story-cta">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <OrderButton location="story" size="lg" />
          <Link to="/visit-us" className="inline-flex items-center justify-center rounded-full border-2 border-hacienda-terracotta text-hacienda-terracotta hover:bg-hacienda-terracotta hover:text-white px-9 py-4 text-lg font-medium transition-colors">{t("nav.visit")}</Link>
        </div>
      </section>
    </div>
  );
};

export default StoryPage;
